import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useHistory, Link } from 'react-router-dom';
import { find } from 'ramda';
import Multilingual from '~~utils/Multilingual';
import { useQuery } from '~~utils/CommonUtils';
import { useTournamentDispatch, useTournamentState } from './useTournament';
import { StyledBackIcon } from './styled';
import Game from './Game';
import Registry from './Registry';
import Basic from './components/Basic';
import GameGroup from './components/GameGroup';
import Stats from './components/Stats';
import Schedule from './components/Schedule';
import Teams from './components/Teams';
import Plugins from './components/Plugins';
import AdminOnly from './components/AdminOnly';
import { StyledDiv } from '../Settings/Styles';
import FormalSchedule from './FormalTournament/FormalSchedule';
import FormalGame from './FormalGame';
import FormalAdminOnly from './FormalTournament/FormalAdminOnly';
import CPBLMergeSection from './FormalTournament/CPBLMergeSection';
import CPBLSeasonTeamPlayerDedupeSection from './FormalTournament/CPBLSeasonTeamPlayerDedupeSection';
import { FormalGameProvider } from './FormalGame/useFormalGame';

const Tournament = ({
  isPlatformAdmin,
  organizer,
  tournament,
  games: originGames,
  groups,
  teams: originTeams,
  locations,
}) => {
  const { section } = useParams();
  const tournamentDispatch = useTournamentDispatch();
  const { isProLeague, tournamentInfo, games, gameGroups, teams } = useTournamentState();

  const query = useQuery();
  const history = useHistory();
  const location = useLocation();
  const gameUniqid = query.get('gid');
  const [targetGame, setTargetGame] = useState(null);

  useEffect(() => {
    tournamentDispatch({
      type: 'SET_ORGANIZER_UNIQID',
      payload: {
        uniqid: organizer.uniqid,
        isFormal: organizer.is_formal,
      },
    });
    tournamentDispatch({ type: 'SET_TOURNAMENT_INFO', payload: tournament });
  }, [organizer, tournament, tournamentDispatch]);

  useEffect(() => {
    tournamentDispatch({ type: 'SET_GAMES', payload: originGames });
  }, [originGames, tournamentDispatch]);

  useEffect(() => {
    tournamentDispatch({ type: 'SET_GAME_GROUPS', payload: groups });
  }, [groups, tournamentDispatch]);

  useEffect(() => {
    tournamentDispatch({ type: 'SET_TEAMS', payload: originTeams });
  }, [originTeams, tournamentDispatch]);

  useEffect(() => {
    tournamentDispatch({ type: 'SET_LOCATIONS', payload: locations });
  }, [locations, tournamentDispatch]);

  useEffect(() => {
    setTargetGame(null);
    if (section !== 'game') {
      return;
    }

    const searchFrom = games.length === 0 ? originGames : games;
    const urlGame = find((g) => g.uniqid === gameUniqid, searchFrom);
    if (!urlGame) {
      history.push(`${location.pathname.slice(0, -5)}/basic`);
      return;
    }

    setTargetGame(urlGame);
  }, [gameUniqid, games, history, location.pathname, originGames, section]);

  const sectionLink = (targetSection) => {
    return (
      <Link
        className={`section-list--item ${section === targetSection && 'selected'}`}
        to={(location) => ({
          ...location,
          pathname: `/organizer/${organizer.uniqid}/tournament/${tournament.uniqid}/${targetSection}`,
        })}
      >
        {Multilingual(`TOURNAMENT.SIDEBAR.${targetSection.toUpperCase()}`)}
      </Link>
    );
  };

  const getSection = () => {
    switch (section) {
      case 'basic':
        return <Basic tournament={tournamentInfo} />;
      case 'stats':
        return <Stats tournament={tournamentInfo} />;
      case 'schedule':
        return <Schedule tournament={tournamentInfo} games={games} groups={gameGroups} teams={teams} />;
      case 'game_group':
        return <GameGroup tournament={tournamentInfo} groups={gameGroups} />;
      case 'teams':
        return <Teams tournament={tournamentInfo} teams={teams} />;
      case 'plugins':
        return <Plugins tournament={tournamentInfo} />;
      case 'platform_admin_only':
        if (!isPlatformAdmin) {
          return <></>;
        }
        return <AdminOnly tournament={tournamentInfo} teams={teams} games={games} />;
      default:
        return <></>;
    }
  };

  const getFormalSection = () => {
    switch (section) {
      case 'basic':
        return <Basic tournament={tournamentInfo} />;
      case 'teams':
        return <Teams tournament={tournamentInfo} teams={teams} />;
      case 'schedule':
        return <FormalSchedule tournament={tournamentInfo} games={games} teams={teams} />;
      case 'formal_admin_only':
        if (!isPlatformAdmin) {
          return <></>;
        }
        return <FormalAdminOnly tournament={tournamentInfo} games={games} />;
      case 'cpbl_merge':
        if (!isPlatformAdmin) {
          return <></>;
        }
        return (
          <>
            <CPBLSeasonTeamPlayerDedupeSection tournament={tournamentInfo} />
            <CPBLMergeSection organizer={organizer} />
          </>
        );
      default:
        return <></>;
    }
  };

  if (targetGame) {
    if (isProLeague) {
      return (
        <FormalGameProvider>
          <FormalGame isPlatformAdmin={isPlatformAdmin} organizer={organizer} game={targetGame} />
        </FormalGameProvider>
      );
    }
    return <Game isPlatformAdmin={isPlatformAdmin} organizer={organizer} game={targetGame} />;
  }

  if (section === 'registration') {
    return <Registry organizer={organizer} />;
  }

  return (
    <StyledDiv>
      <div className="greet">
        {organizer.logo_url ? (
          <img className="greet--pic" src={organizer.logo_url} alt="" />
        ) : (
          <div className="greet--pic default"></div>
        )}
        <a href={`${process.env.REACT_APP_MAINPAGE_URL}/league/${organizer.uniqid}`} target="_blank" rel="noreferrer">
          <div className="greet--name">
            {organizer.name}
            {' - '}
            {tournamentInfo.title}
          </div>
        </a>
      </div>
      <div className="panel">
        <div className="multi-section-list">
          <div className="section-list">
            <div className="section-list--title" />
            <Link
              className="section-list--item"
              to={() => ({
                pathname: `/settings/organizer_tournament_management`,
              })}
            >
              <StyledBackIcon />
              {Multilingual(`ACCOUNT_MANAGEMENT.WORDING`)}
            </Link>
          </div>
          <div className="section-list">
            <div className="section-list--title" />
            {!isProLeague ? (
              <>
                {sectionLink('basic')}
                {sectionLink('stats')}
                {sectionLink('schedule')}
                {sectionLink('game_group')}
                {sectionLink('teams')}
                {sectionLink('plugins')}
                {tournamentInfo.config.open_registration && sectionLink('registration')}
                {isPlatformAdmin && sectionLink('platform_admin_only')}
              </>
            ) : (
              <>
                {sectionLink('basic')}
                {sectionLink('teams')}
                {sectionLink('schedule')}
                {sectionLink('formal_admin_only')}
                {isPlatformAdmin && isProLeague && sectionLink('cpbl_merge')}
              </>
            )}
          </div>
        </div>
        <div className="section">
          <div className="section--title">{Multilingual(`TOURNAMENT.SIDEBAR.${section.toUpperCase()}`)}</div>
          {!isProLeague ? getSection() : getFormalSection()}
        </div>
      </div>
    </StyledDiv>
  );
};

export default Tournament;
